import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';

import { ConfiguratorIntegrations } from '@core/models';
import { environment } from '@environments/environment';
import { ApiService, DefaultHttpHeaders } from '@services/api/api.service';
import { catchError, finalize, retry, tap } from 'rxjs/operators';
import { SetConfiguratorPartnership } from '@store/flow-data/configurator';
import { DEFAULT_PARTNERSHIP_ID_VERTIWEB, DEFAULT_PARTNERSHIP_SHORTNAME_VERTIWEB } from '@global/constants';
import {
  AffinityAllianceRiskTypeConfiguration,
  PlainAllianceAffinityConfiguration,
  GenesysChat,
} from '@core/models/configurator';
import { getDate } from '@fwmovilidad/qb-home-ita-components-lib';
import { ConfigPartnershipCard } from '@core/models/configPartnershipCard';
import { GenesysChatInfoData } from '@store/flow-data/general';

@Injectable({
  providedIn: 'root',
})
export class ConfiguratorService extends ApiService {
  private readonly configurationGenesysChat: GenesysChat | undefined;
  getGenesysChat(): Observable<GenesysChat> {
    if (!this.configurationGenesysChat) {
      return this.get<GenesysChat>({
        suffix: '/configurator/auto/genesysChat',
        apiUrl: environment.configuratorApiUrl,
      }).pipe(
        tap(response => this.store.dispatch(GenesysChatInfoData({ data: response }))),
        retry(1),
        catchError(() => {
          return EMPTY;
        })
      );
    }
    return of(this.configurationGenesysChat);
  }

  private readonly _getAffinityConfiguration$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly getAffinityConfigurationLoading$ = this._getAffinityConfiguration$.asObservable();
  private affinityConfigurationResponse: PlainAllianceAffinityConfiguration;
  getAllianceAffinityConfiguration({
    allianceCode = DEFAULT_PARTNERSHIP_SHORTNAME_VERTIWEB,
    affinityCode = DEFAULT_PARTNERSHIP_ID_VERTIWEB,
    date = getDate(),
    risk,
  }: {
    allianceCode?: string;
    affinityCode?: string;
    date?: Date;
    risk: AffinityAllianceRiskTypeConfiguration;
  }): Observable<PlainAllianceAffinityConfiguration> {
    this._getAffinityConfiguration$.next(true);
    if (typeof date === 'string') {
      date = new Date(date);
    }
    return this.get<PlainAllianceAffinityConfiguration>({
      apiUrl: environment.partnershipApiUrl,
      suffix: `/configurations/alliance/${allianceCode}/affinity/${affinityCode}/configuration`,
      queryParams: {
        date: date.toISOString(),
        risk,
      },
    }).pipe(
      catchError(error => {
        return EMPTY;
      }),
      tap(response => {
        this.affinityConfigurationResponse = response;
        const partnershipCard: ConfigPartnershipCard = {
          partnershipId: response?.affinityCode,
          partnershipName: response?.affinityName,
          partnershipUrl: response?.image?.redirectionUrl,
          partnershipImage: response?.image?.imageUrl,
          partnershipDocument: response?.document?.documentUrl,
        };
        this.store.dispatch(SetConfiguratorPartnership({ data: partnershipCard }));
      }),
      retry(1),
      finalize(() => this._getAffinityConfiguration$.next(false))
    );
  }
}

export interface GetPartnershipParams extends DefaultHttpHeaders {
  partnershipId: string;
}
